import { combineLatest, forkJoin, iif as observableIif, Observable, of } from 'rxjs';
import { Component, OnInit, ViewChild } from '@angular/core';
import { UserInfo } from '../shared/user-info.model';
import { DisplayMode } from '../../shared/display-mode';
import { ActivatedRoute } from '@angular/router';
import { LoaderService } from '../../shared/services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { DriverService } from '../shared/driver.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CarrierService } from '../shared/carrier.service';
import { Carrier } from '../shared/carrier.model';
import { UserService } from '../shared/user.service';
import { UserFormComponent } from '../user-form/user-form.component';
import { ComponentCanDeactivate } from '../../core/guards/pending-changes.guard';
import { NavigationService } from '../../shared/services/navigation.service';
import { SubcontractorService } from '../shared/subcontractor.service';
import { Subcontractor } from '../shared/subcontractor.model';
import { finalize, map, tap } from 'rxjs/operators';

@Component({
  selector: 'app-driver',
  templateUrl: './driver.component.html',
  styleUrls: ['./driver.component.scss']
})
export class DriverComponent implements OnInit, ComponentCanDeactivate {

  user = new UserInfo();
  carrier: Carrier;
  subcontractor: Subcontractor;

  subcontractorMode = false;
  selfCarrierMode   = false;

  onCreate: (u: UserInfo) => Observable<UserInfo>;
  onUpdate: (u: UserInfo) => Observable<UserInfo>;
  onDelete: (u: UserInfo) => Observable<boolean>;
  onToggleBlocking: (u: UserInfo) => Observable<boolean>;
  onUnblockIp: (u: UserInfo) => Observable<boolean>;

  mode = DisplayMode.Creation;

  DisplayMode = DisplayMode;

  formTitle = 'New Driver';

  @ViewChild('userForm')
  userFormComponent: UserFormComponent;

  constructor(private route: ActivatedRoute,
              private navigation: NavigationService,
              private userService: UserService,
              private driverService: DriverService,
              private carrierService: CarrierService,
              private subcontractorService: SubcontractorService,
              private loader: LoaderService,
              private toastr: ToastrService
  ) {
    this.onCreate = u => this.driverService.createForSelfCarrier(u);

    this.onUpdate = u => this.driverService.update(u);

    this.onDelete = u => this.driverService.delete(u.id);

    this.onToggleBlocking = u => this.driverService.toggleBlocking(u);

    this.onUnblockIp = u => this.driverService.unblockIp(u);
  }

  ngOnInit() {
    combineLatest([this.route.params, this.route.data])
      .subscribe(([params, data]) => {
        const carrierId       = params.carrierId;
        const subcontractorId = params.subcontractorId;
        const driverId        = params.driverId;

        if (data.selfCarrierMode) {
          this.selfCarrierMode = true;
        }

        if (data.subcontractorMode) {
          this.subcontractorMode = true;
        }

        this.loader.startLoad(false);

        const driverObservable = observableIif(() => !!driverId,
          this.driverService.getById(driverId).pipe(tap(() => this.switchToUpdateMode())),
          of(new UserInfo())
        );

        const carrierObservable = observableIif(() => !!carrierId,
          this.carrierService.getById(carrierId).pipe(tap(carrier => this.switchToCarrierMode(carrier))),
          this.userService.getSelf().pipe(map(currentUser => currentUser.carrier))
        );

        const subcontractorObservable = observableIif(() => !!subcontractorId,
          this.subcontractorService.getById(carrierId, subcontractorId)
            .pipe(tap(subcontractor => this.switchToSubcontractorMode(subcontractor))),
          of(null)
        );

        forkJoin([driverObservable, carrierObservable, subcontractorObservable])
          .pipe(finalize(() => this.loader.stopLoad()))
          .subscribe(([driver, carrier, subcontractor]) => {
            this.user               = driver;
            this.user.carrier       = carrier;
            this.user.subcontractor = subcontractor;

            this.carrier       = carrier;
            this.subcontractor = subcontractor;
          }, (error: HttpErrorResponse) => {
            console.log(error);
            this.toastr.error(error.error.message || 'An error occured while fetching the user', 'Error');
          });
      });
  }

  getForm() {
    return this.userFormComponent.form;
  }

  switchToUpdateMode() {
    this.mode      = DisplayMode.Edition;
    this.formTitle = 'Driver Edition';
  }

  switchToCarrierMode(carrier: Carrier) {
    if (!this.subcontractor) {
      this.onCreate = u => this.driverService.createForCarrier(u, carrier.id);
    }
  }

  switchToSubcontractorMode(subcontractor: Subcontractor) {
    this.onCreate = u => this.driverService.createForSubcontractor(u, subcontractor.id);
  }

  goToDriverList() {
    if (this.subcontractorMode) {

      if (this.selfCarrierMode) {
        this.navigation.selfCarrier()
          .subcontractors()
          .drivers(this.subcontractor.id)
          .list()
          .go();
      } else {
        this.navigation.carriers()
          .subcontractors(this.carrier.id)
          .drivers(this.subcontractor.id)
          .list()
          .go();
      }

    } else if (this.selfCarrierMode) {

      this.navigation.selfCarrier()
        .drivers()
        .list()
        .go();

    } else {

      this.navigation.carriers()
        .drivers(this.carrier.id)
        .list()
        .go();

    }
  }

  changePassword(password: string) {
    this.loader.startLoad();

    this.driverService.updatePassword(this.user, password)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {
        this.toastr.success('The password has been successfully updated', 'Success');
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.toastr.error(error.error.message || 'Error while updating the password', 'Error');
      });
  }

}
